import { AfterViewChecked, Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Unsubscribe } from 'firebase/auth';
import { marked } from 'marked';
import { Subscription } from 'rxjs';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { environment } from 'src/environments/environment';
import { SupportChatbotService } from './support-chatbot.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const FUNCTIONS_REGION = 'northamerica-northeast1';

@Component({
  selector: 'app-support-chatbot',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  styles: [`
    :host { display: contents; }
    .sc-panel {
      position: fixed; bottom: 1.25rem; right: 1.25rem; z-index: 1000;
      width: 22rem; max-width: calc(100vw - 2rem);
      height: 28rem; max-height: calc(100vh - 6rem);
      background: var(--background-color);
      color: var(--text-color);
      border: 1px solid var(--border-color);
      border-radius: 0.75rem;
      box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
      display: flex; flex-direction: column; overflow: hidden;
      -webkit-font-smoothing: auto;
      -moz-osx-font-smoothing: auto;
    }
    .sc-header {
      display:flex; align-items:center; justify-content:space-between;
      padding: 0.75rem 1rem;
      border-bottom: 1px solid var(--border-color);
      font-weight:600; font-size:0.875rem; line-height: 1.25rem;
      color: var(--text-color);
    }
    .sc-header-actions { display: flex; gap: 0.25rem; }
    .sc-header button {
      background:none; border:none; cursor:pointer;
      color: var(--text-secondary-color, #6b7280);
      padding: 0.25rem; border-radius: 0.25rem;
      transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }
    .sc-header button:hover { background: var(--surface-color); }
    .sc-stop {
      font-size: 0.75rem;
      padding: 0.125rem 0.5rem !important;
    }
    .sc-body {
      flex:1; overflow-y:auto;
      padding: 0.75rem 1rem;
      display:flex; flex-direction:column; gap: 0.75rem;
      font-size: 0.875rem; line-height: 1.25rem;
    }
    .sc-empty {
      text-align:center;
      color: var(--text-secondary-color, #6b7280);
      margin-top: 2rem;
    }
    .sc-body-centered {
      justify-content: center;
      align-items: center;
    }
    .sc-no-credits {
      display: flex; flex-direction: column; align-items: center;
      gap: 1rem; text-align: center; padding: 0 1rem;
    }
    .sc-no-credits p {
      color: var(--text-secondary-color, #6b7280);
      margin: 0; line-height: 1.4;
    }
    .sc-buy-credits {
      display: inline-flex; align-items: center; justify-content: center;
      background: #4c17ca; color: #fff !important; text-decoration: none;
      padding: 0.5rem 1rem; border-radius: 0.5rem;
      font-size: 0.875rem; font-weight: 500;
      transition: background-color 0.15s; cursor: pointer;
    }
    .sc-buy-credits:hover { background: #3d12a2; }
    .sc-row { display:flex; }
    .sc-row.user { justify-content:flex-end; }
    .sc-bubble {
      max-width:85%;
      padding: 0.5rem 0.75rem;
      border-radius: 1rem;
      white-space:pre-wrap;
      word-break:break-word;
      font-size: 0.875rem;
      line-height: 1.5;
    }
    .sc-row.user .sc-bubble {
      background: var(--surface-color);
      color: var(--text-color);
    }

    /* Assistant: no bubble, plain text with markdown styles */
    .sc-assistant {
      max-width: 100%;
      color: var(--text-color);
      font-size: 0.875rem;
      line-height: 1.5;
    }
    .sc-assistant :first-child { margin-top: 0; }
    .sc-assistant :last-child  { margin-bottom: 0; }
    .sc-assistant p { margin: 0.5rem 0; }
    .sc-assistant h1, .sc-assistant h2, .sc-assistant h3,
    .sc-assistant h4, .sc-assistant h5, .sc-assistant h6 {
      font-weight: 600; margin: 0.85rem 0 0.35rem; line-height: 1.3;
    }
    .sc-assistant h1 { font-size: 1.1rem; }
    .sc-assistant h2 { font-size: 1.02rem; }
    .sc-assistant h3 { font-size: 0.95rem; }
    .sc-assistant h4, .sc-assistant h5, .sc-assistant h6 { font-size: 0.9rem; }
    .sc-assistant ul, .sc-assistant ol { margin: 0.5rem 0; padding-left: 1.5rem; }
    .sc-assistant ul { list-style: disc outside; }
    .sc-assistant ol { list-style: decimal outside; }
    .sc-assistant ul ul { list-style: circle outside; }
    .sc-assistant ul ul ul { list-style: square outside; }
    .sc-assistant ul ul, .sc-assistant ol ol,
    .sc-assistant ul ol, .sc-assistant ol ul { margin: 0.25rem 0; }
    .sc-assistant li { margin: 0.2rem 0; padding-left: 0.15rem; }
    .sc-assistant li > p { margin: 0.2rem 0; }
    .sc-assistant li::marker { color: var(--text-secondary-color, #6b7280); }
    .sc-assistant a { color: #4c17ca; text-decoration: underline; text-underline-offset: 2px; }
    .sc-assistant a:hover { text-decoration-thickness: 2px; }
    .sc-assistant strong { font-weight: 600; }
    .sc-assistant em { font-style: italic; }
    .sc-assistant code {
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 0.85em;
      background: rgba(127, 127, 127, 0.18);
      padding: 0.1em 0.35em;
      border-radius: 0.25rem;
    }
    .sc-assistant pre {
      background: rgba(127, 127, 127, 0.15);
      padding: 0.65rem 0.75rem;
      border-radius: 0.5rem;
      overflow-x: auto;
      margin: 0.6rem 0;
      font-size: 0.85em;
      line-height: 1.5;
    }
    .sc-assistant pre code { background: transparent; padding: 0; font-size: 1em; }
    .sc-assistant blockquote {
      border-left: 3px solid rgba(127, 127, 127, 0.4);
      padding: 0.1rem 0 0.1rem 0.65rem;
      margin: 0.5rem 0;
      color: var(--text-secondary-color, #6b7280);
    }
    .sc-assistant hr { border: none; border-top: 1px solid rgba(127, 127, 127, 0.3); margin: 0.75rem 0; }
    .sc-assistant table {
      border-collapse: collapse;
      margin: 0.5rem 0;
      font-size: 0.9em;
      width: 100%;
    }
    .sc-assistant th, .sc-assistant td {
      border: 1px solid rgba(127, 127, 127, 0.3);
      padding: 0.3rem 0.5rem;
      text-align: left;
    }
    .sc-assistant th { background: rgba(127, 127, 127, 0.1); font-weight: 600; }

    .sc-error { color: oklch(63.7% 0.237 25.331); font-size: 0.75rem; }
    .sc-footer { padding: 0.5rem; }
    .sc-inputrow {
      display: flex; align-items: flex-end; gap: 0.5rem;
      width: 100%;
      padding: 0.375rem 0.5rem;
      border-radius: 0.5rem;
      border: 1px solid var(--border-color);
      background-color: color-mix(in srgb, var(--surface-color) 50%, transparent);
      backdrop-filter: blur(4px);
      transition: border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }
    .sc-inputrow:hover,
    .sc-inputrow:focus-within {
      border-color: color-mix(in srgb, var(--text-secondary-color) 40%, transparent);
    }
    .sc-inputrow textarea {
      flex:1; resize:none; border:none; background:transparent;
      color: var(--text-color);
      font-size: 0.875rem;
      line-height: 1.375;
      padding: 0.25rem 0;
      max-height: 10rem;
      overflow: auto;
      outline:none;
      font-family: 'Inter', system-ui, sans-serif;
    }
    .sc-inputrow textarea::placeholder {
      color: color-mix(in oklab, var(--text-secondary-color) 60%, transparent);
    }
    .sc-send {
      flex-shrink: 0;
      background: #4c17ca;
      color: #fff;
      border: none;
      border-radius: 9999px;
      width: 2rem; height: 2rem;
      padding: 0;
      cursor: pointer;
      transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
      display: inline-flex; align-items: center; justify-content: center;
    }
    .sc-send:hover:not(:disabled) { background: #3d12a2; }
    .sc-send:disabled {
      background: var(--border-color);
      color: var(--text-secondary-color);
      cursor: default;
    }
    .sc-send svg { width: 1.25rem; height: 1.25rem; }
    .sc-spinner {
      width: 1rem; height: 1rem; border-radius: 9999px;
      border: 2px solid currentColor; border-top-color: transparent;
      animation: sc-spin 1s linear infinite;
    }
    @keyframes sc-spin { to { transform: rotate(360deg); } }

    .sc-typing { display:inline-flex; align-items:center; gap: 0.25rem; vertical-align:middle; }
    .sc-typing-dot {
      display:inline-block; width: 0.375rem; height: 0.375rem; border-radius: 9999px;
      background-color: var(--text-secondary-color, #6b7280);
      animation: sc-typing 1.4s infinite;
    }
    .sc-typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .sc-typing-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes sc-typing {
      0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
      30%           { opacity: 1;   transform: translateY(-2px); }
    }
  `],
  template: `
    <div class="sc-panel" *ngIf="open">
        <div class="sc-header">
          <span>{{ 'support-chat.title' | translate }}</span>
          <div class="sc-header-actions">
            <button *ngIf="sending" type="button" class="sc-stop" (click)="cancel()" [attr.aria-label]="'support-chat.stop' | translate">
              {{ 'support-chat.stop' | translate }}
            </button>
            <button type="button" (click)="toggle()" [attr.aria-label]="'support-chat.closeAria' | translate">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                   fill="none" stroke="currentColor" stroke-width="2"
                   width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
        <div #scroll class="sc-body" [class.sc-body-centered]="messages.length === 0 && !hasCredits">
          <div class="sc-empty" *ngIf="messages.length === 0 && hasCredits">{{ 'support-chat.greeting' | translate }}</div>
          <div class="sc-no-credits" *ngIf="messages.length === 0 && !hasCredits">
            <p>{{ 'support-chat.no-credits' | translate }}</p>
            <a class="sc-buy-credits" [href]="billingUrl" target="_blank" rel="noopener">
              {{ 'support-chat.buy-credits' | translate }}
            </a>
          </div>
          <ng-container *ngFor="let msg of messages; let i = index">
            <div *ngIf="msg.role === 'user'" class="sc-row user">
              <div class="sc-bubble">{{ msg.content }}</div>
            </div>
            <div *ngIf="msg.role !== 'user'" class="sc-assistant">
              <div *ngIf="msg.content" [innerHTML]="renderMarkdown(msg.content)"></div>
              <span *ngIf="!msg.content && sending" class="sc-typing" aria-hidden="true">
                <span class="sc-typing-dot"></span>
                <span class="sc-typing-dot"></span>
                <span class="sc-typing-dot"></span>
              </span>
            </div>
          </ng-container>
          <div class="sc-error" *ngIf="errorMsg">{{ errorMsg }}</div>
        </div>
        <div class="sc-footer">
          <div class="sc-inputrow">
            <textarea
              [(ngModel)]="input"
              (keydown)="onKeydown($event)"
              [disabled]="sending || !hasCredits"
              rows="1"
              [placeholder]="'support-chat.placeholder' | translate"
            ></textarea>
            <button
              type="button"
              class="sc-send"
              (click)="send()"
              [disabled]="sending || !input.trim() || !hasCredits"
              [attr.aria-label]="'support-chat.send' | translate"
            >
              <span *ngIf="sending" class="sc-spinner"></span>
              <svg *ngIf="!sending" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10 17a.75.75 0 0 1-.75-.75V5.56L5.22 9.59a.75.75 0 1 1-1.04-1.08
                     l5.25-5.25a.75.75 0 0 1 1.04 0l5.25 5.25a.75.75 0 1 1-1.04 1.08
                     L10.75 5.56v10.69A.75.75 0 0 1 10 17Z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
  `,
  encapsulation: ViewEncapsulation.None,
})
export class SupportChatbotComponent implements AfterViewChecked, OnDestroy {
  @ViewChild('scroll') scroll?: ElementRef<HTMLDivElement>;

  open = false;
  messages: ChatMessage[] = [];
  sending = false;
  errorMsg = '';
  authed = false;
  input = '';

  readonly billingUrl = `${environment.adminConsoleUrl}/billing`;

  get hasCredits(): boolean {
    return this.organization.canConsumeCredits();
  }

  private shouldScroll = false;
  private sessionId: string | null = null;
  private abortController: AbortController | null = null;
  private unsubscribe?: Unsubscribe;
  private openSub?: Subscription;

  constructor(
    private firebase: FirebaseService,
    private supportChatbot: SupportChatbotService,
    private translate: TranslateService,
    private organization: OrganizationService,
  ) {
    this.unsubscribe = this.firebase.auth.onAuthStateChanged((user) => {
      this.authed = !!user && !user.isAnonymous;
    });
    this.openSub = this.supportChatbot.open$.subscribe((v) => {
      this.open = v;
      this.shouldScroll = true;
    });
  }

  ngOnDestroy() {
    this.unsubscribe?.();
    this.openSub?.unsubscribe();
    this.abortController?.abort();
  }

  toggle() {
    const willOpen = !this.open;
    this.supportChatbot.toggle();
    if (willOpen) {
      if (!this.sessionId) this.sessionId = this.generateSessionId();
    } else {
      this.resetSession();
    }
  }

  ngAfterViewChecked() {
    if (this.shouldScroll && this.scroll) {
      this.scroll.nativeElement.scrollTop = this.scroll.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  renderMarkdown(text: string): string {
    return marked.parse(text, { async: false, breaks: true, gfm: true }) as string;
  }

  cancel() {
    this.abortController?.abort();
  }

  async send() {
    const text = this.input.trim();
    if (!text || this.sending || !this.hasCredits) return;
    if (!this.sessionId) this.sessionId = this.generateSessionId();

    this.errorMsg = '';
    this.messages = [
      ...this.messages,
      { role: 'user', content: text },
      { role: 'assistant', content: '' },
    ];
    this.input = '';
    this.sending = true;
    this.shouldScroll = true;

    try {
      await this.stream(text);
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        this.errorMsg = err?.message || this.translate.instant('support-chat.error');
      }
    } finally {
      this.sending = false;
      this.abortController = null;
      this.shouldScroll = true;
    }
  }

  private async stream(message: string): Promise<void> {
    const user = this.firebase.auth.auth.currentUser;
    if (!user) {
      this.errorMsg = this.translate.instant('support-chat.notAuthenticated');
      return;
    }
    const idToken = await user.getIdToken();
    const projectId = environment.firebaseConfig.projectId;
    const endpoint = `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/runSupportAgent`;

    this.abortController = new AbortController();
    const resp = await fetch(endpoint, {
      method: 'POST',
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        sessionId: this.sessionId,
        message,
        app: 'connect',
      }),
      signal: this.abortController.signal,
    });

    if (resp.status === 402) {
      throw new Error(this.translate.instant('support-chat.no-credits'));
    }
    if (!resp.ok || !resp.body) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Server ${resp.status}: ${body || resp.statusText}`);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      // eslint-disable-next-line no-cond-assign
      while ((nl = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        for (const line of frame.split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            this.handleEvent(event);
          } catch {
            // Ignore malformed frame
          }
        }
      }
    }
  }

  private handleEvent(event: any): void {
    const type = event?.type as string | undefined;
    if (!type) return;

    if (type === 'token' && typeof event.token === 'string') {
      this.appendToLastAssistant(event.token);
      this.shouldScroll = true;
      return;
    }

    if (type === 'error' && typeof event.error === 'string') {
      const code = event.error;
      const knownKey = `support-chat.${code}`;
      const translated = this.translate.instant(knownKey);
      this.errorMsg = translated && translated !== knownKey ? translated : code;
    }
  }

  private appendToLastAssistant(chunk: string) {
    const last = this.messages[this.messages.length - 1];
    if (!last || last.role !== 'assistant') {
      this.messages = [...this.messages, { role: 'assistant', content: chunk }];
      return;
    }
    const updated: ChatMessage = { role: 'assistant', content: last.content + chunk };
    this.messages = [...this.messages.slice(0, -1), updated];
  }

  private generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  private resetSession() {
    this.abortController?.abort();
    this.abortController = null;
    this.sessionId = null;
    this.messages = [];
    this.errorMsg = '';
    this.sending = false;
  }
}
